import { API_ROUTES, postEncryptedJson } from "../utils/apiClient";
import AppConstants from "../utils/appConstants";

/**
 * Exchange Controller
 * Encapsulates all exchange-related API calls (currencies, rates, calculate).
 * Uses the shared encrypted transport in apiClient.js.
 */
class ExchangeController {
  /**
   * Get list of exchangeable currencies.
   * Matches Flutter: shows all currencies from DATA array, no filtering.
   * @returns {Promise<{ currencies: Array, raw: any }>} currencies with basic fields
   */
  static async getExchangeCurrencies(options = {}) {
    try {
      const res = await postEncryptedJson(API_ROUTES.GET_EXCHANGE_CURRENCIES, {}, options);
      const d = res.decryptedData || res.data || {};
      
      
      if (!d || Number(d.CODE) !== AppConstants.responseOk) {
        const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
        console.error("Exchange currencies API error:", msg, d);
        throw new Error(msg);
      }

      // Match Flutter: process all items from DATA array (no filtering by IS_ENABLE_EXCHANGE)
      const list = Array.isArray(d.DATA) ? d.DATA : [];
      
      if (list.length === 0) {
        console.warn("Exchange currencies: DATA array is empty or not an array", {
          DATA: d.DATA,
          fullResponse: d,
        });
      }
      
      const currencies = list.map((item, index) => {
        // Extract ID (matches Flutter: currencyTemp.id from CurrencyModel.fromJson)
        // Try multiple possible field names
        const id = item.ID != null ? String(item.ID) 
                 : item.id != null ? String(item.id)
                 : item.CURRENCY_ID != null ? String(item.CURRENCY_ID)
                 : item.currencyId != null ? String(item.currencyId)
                 : null;
        
        // Extract currency code (matches Flutter: currencyTemp.currencyCode)
        const code = item.CURRENCY_CODE || item.currencyCode || item.CODE || item.code || "";
        
        // Extract currency symbol (for reference, but Flutter shows code twice)
        const symbol = item.CURRENCY_SYMBOL || item.currencySymbol || item.SYMBOL || item.symbol || "";
        
        // Extract other fields that might be needed
        const country = item.COUNTRY || item.country || "";
        
        if (!id) {
          console.warn(`Exchange currency at index ${index} has no ID:`, item);
        }
        if (!code) {
          console.warn(`Exchange currency at index ${index} has no currency code:`, item);
        }
        
        return {
          id,
          code,
          symbol,
          country,
          // Store full raw item (matches Flutter: value: currencyTemp)
          raw: item,
        };
      });

      return { currencies, raw: d };
    } catch (error) {
      console.error("ExchangeController.getExchangeCurrencies error:", error);
      throw error;
    }
  }

  /**
   * Get exchange rates between two currencies.
   * @param {string|number} fromCurrencyId
   * @param {string|number} toCurrencyId
   * @returns {Promise<{ ratesMap: Record<string, number>, raw: any }>}
   */
  static async getExchangeRates(fromCurrencyId, toCurrencyId, options = {}) {
    // Convert to numbers to match API spec (FROM_CURRENCY: 1, TO_CURRENCY: 3)
    const fromId = typeof fromCurrencyId === 'string' ? parseInt(fromCurrencyId, 10) : Number(fromCurrencyId);
    const toId = typeof toCurrencyId === 'string' ? parseInt(toCurrencyId, 10) : Number(toCurrencyId);
    
    const body = {
      FROM_CURRENCY: fromId,
      TO_CURRENCY: toId,
    };
    const res = await postEncryptedJson(API_ROUTES.GET_EXCHANGE_RATES, body, options);
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    const ratesMap = {};
    const list = Array.isArray(d.DATA) ? d.DATA : [];
    list.forEach((el) => {
      const id = el.ID != null ? String(el.ID) : el.id != null ? String(el.id) : null;
      let rate = el.RATE;
      if (typeof rate === "string") {
        const parsed = parseFloat(rate);
        rate = Number.isNaN(parsed) ? null : parsed;
      }
      if (id && typeof rate === "number") {
        ratesMap[id] = rate;
      }
    });

    return { ratesMap, raw: d };
  }

  /**
   * Calculate exchange for the given amount and currencies.
   * Returns EXCHANGE_ID and AMOUNT_AFTER_EXCHANGE from backend.
   * @param {number} amount
   * @param {string|number} fromCurrencyId
   * @param {string|number} toCurrencyId
   */
  static async calculateExchange(amount, fromCurrencyId, toCurrencyId, options = {}) {
    // Convert to numbers to match API spec
    const fromId = typeof fromCurrencyId === 'string' ? parseInt(fromCurrencyId, 10) : Number(fromCurrencyId);
    const toId = typeof toCurrencyId === 'string' ? parseInt(toCurrencyId, 10) : Number(toCurrencyId);
    
    const body = {
      AMOUNT: typeof amount === 'number' ? amount : parseFloat(String(amount).replace(/,/g, '')),
      FROM_CURRENCY: fromId,
      TO_CURRENCY: toId,
    };

    const res = await postEncryptedJson(API_ROUTES.CALCULATE_EXCHANGE_RATE, body, options);
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    const exchangeId =
      d.EXCHANGE_ID != null
        ? String(d.EXCHANGE_ID)
        : d.exchangeId != null
        ? String(d.exchangeId)
        : null;

    let amountAfter = d.AMOUNT_AFTER_EXCHANGE ?? d.amountAfterExchange ?? 0;
    if (typeof amountAfter === "string") {
      const parsed = parseFloat(amountAfter.replace(/,/g, ""));
      amountAfter = Number.isNaN(parsed) ? 0 : parsed;
    }

    return {
      exchangeId,
      amountAfterExchange: typeof amountAfter === "number" ? amountAfter : 0,
      raw: d,
    };
  }
}

export default ExchangeController;

